import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Customer,
  FpCompany,
  FpTheme,
  Job,
  PrimaryTheme,
  usePreloaderContext,
} from '@flicent/fieldpulse-react-component-library-frontend';

import { JobPageContent, JobReport } from '@/components';
import { getPublicLinkJobMetaRequest, signAndAcceptJobRequest } from '@/services';
import { MetaDataPublicLink } from '@/interfaces';
import { JobPublicLinkStatus } from '@/types';
import { JOB_PUBLIC_LINK_STATUS } from '@/constants';


const PublicJobReport = () => {
  const { publicLinkId } = useParams();

  const [metaData, setMetaData] = useState<MetaDataPublicLink | null>(null);
  const [theme, setTheme] = useState<FpTheme>(PrimaryTheme);

  const preloaderContext = usePreloaderContext();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string>('');

  const [signatureResponseStatus, setSignatureResponseStatus] = useState<JobPublicLinkStatus>();

  // Fetch metadata based on {publicLinkId}
  useEffect(() => {
    if (!publicLinkId) {
      return;
    }

    setLoading(true);

    getPublicLinkJobMetaRequest(publicLinkId)
      .then(({ meta, theme }) => {
        setMetaData(meta);
        setTheme(theme);
        setPdfUrl(meta.public_link_additional_data.pdf_url || '');
        setSignatureResponseStatus(meta.public_link_status);
      })
      .catch((error) => {
        setError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [publicLinkId]);

  // Control full page preloaderContext based on {metaData} availability
  useEffect(() => {
    if (loading) {
      preloaderContext.showFullPagePreloader();
    } else {
      preloaderContext.hideFullPagePreloader();
    }
  }, [loading]);


  const acceptSignatures = async (signatures: string[]) => {
    preloaderContext.showMiddlePagePreloader();

    const acceptSignaturesResponse = await signAndAcceptJobRequest({
      publicLinkId: publicLinkId!,
      signatures,
    }).finally(() => {
      preloaderContext.hideMiddlePagePreloader();
    });

    setSignatureResponseStatus(acceptSignaturesResponse.status);
    setPdfUrl(acceptSignaturesResponse.pdf_url);

    return acceptSignaturesResponse;
  };

  let contentComponent;

  if (metaData) {
    const isSignaturesAccepted = metaData.public_link_status === JOB_PUBLIC_LINK_STATUS.accepted || signatureResponseStatus === JOB_PUBLIC_LINK_STATUS.accepted;

    const jobs: Job[] = metaData.jobs;
    const company: FpCompany = metaData.company;
    const customer: Customer = metaData.customer;
    const isShowSignatureRequest: boolean = metaData.public_link_additional_data.is_show_signature_request;

    contentComponent = (
      <JobReport
        company={ company }
        customer={ customer }
        jobs={ jobs }

        isShowSignatureRequest={ isShowSignatureRequest }
        isSignaturesAccepted={ isSignaturesAccepted }
        acceptedSignaturesPdfUrl={ pdfUrl }
        onSignaturesAccept={ acceptSignatures }
      />
    );
  }

  return (
    <JobPageContent isLoading={ !metaData }
                    companyJobName={ metaData ? metaData.company.job_name : null }
                    error={ error }
                    theme={ theme }>
      { contentComponent }
    </JobPageContent>
  );
};


export default PublicJobReport;
